// #7133 Phase 2 — a workspace artifact's stored value stays a plain string (no VMR migration), so a new
// file's metadata rides as a single-line JSON header at the very start of that string. Old artifacts carry
// no header and must keep rendering exactly as they do today via extractUrl/fileTypeKind inference — this
// module is additive, never required. Duplicated (not shared via common-lib) to match this file's siblings
// (artifact-image.ts, artifact-file.ts) which keep the browser bundle free of common-lib.

export interface FileRecordHeader {
  scope: 'chat' | 'workspace';
  ownerKind: 'user' | 'agent' | 'system';
  mimeType?: string;
  sizeBytes?: number;
  rawUrl?: string;
  // A "wixel asset" — a Wix design-tool asset carrying a preview image + viewer link + editor link.
  // These 4 field names are a byte-identical cross-surface contract (web/harness/iOS/Android each carry
  // their own hand-duplicated copy — there is no shared type to enforce the spelling): `wixelAssetId`,
  // `wixelViewerUrl`, `wixelEditorUrl`, `wixelPreviewUrl`. Four dedicated fields, not reusing `rawUrl` —
  // `rawUrl` feeds `docSource`'s fetch-as-text logic, and a Wixel viewer link is a navigation target, not
  // a document to fetch.
  wixelAssetId?: string;
  wixelViewerUrl?: string;
  wixelEditorUrl?: string;
  wixelPreviewUrl?: string;
  // WHICH AGENT BUILT THIS FILE — the gallery's "Built by" column, and the agent tag on every file card.
  //
  // ⚠️ NOTHING WRITES THESE YET, so `builtByOf` returns undefined for every file today and the column
  // reads "—" for all of them. That is deliberate and visible rather than hidden: the column belongs to
  // the design, so it renders, and it fills in on its own the moment a writer starts stamping the header.
  //
  // The write side is a HARNESS change (`orion-harness-poc`), deliberately not in this change: the only
  // moment the acting agent is known is when the artifact is written, and `GetWorkspaceArtifacts` carries
  // no attribution field for it to travel in — which is why the header is the right place. When it lands
  // it should write the PAIR or neither, and only for a real agent entity (`ctx.subAgentAgentId`, a
  // sub-agent running AS a named team agent); the primary assistant is a display name with no agent id, so
  // its files stay unattributed rather than credited to a team member who did not write them.
  //
  // Both native clients already parse the header and already have the `agentId`/`agentName` fields their
  // card waits on — they just need these two names added to their own copies, the same way the wixel
  // fields are hand-duplicated. Spell them EXACTLY `agentId` / `agentName` there.
  agentId?: string;
  agentName?: string;
}

const HEADER_RE = /^<!--sym-file:(.+?)-->\n?/;

function isFileRecordHeader(value: unknown): value is FileRecordHeader {
  return !!value && typeof value === 'object' && typeof (value as FileRecordHeader).scope === 'string' && typeof (value as FileRecordHeader).ownerKind === 'string';
}

/** Parses a workspace artifact's raw content: the FileRecord header when present (and well-formed), plus
 *  the body with that header stripped. Absent/malformed header → `header: null`, `body` is the content
 *  UNCHANGED — every existing inference path keeps working on the full string exactly as before. */
export function parseFileRecordContent(content: string | undefined | null): { header: FileRecordHeader | null; body: string } {
  if (!content) return { header: null, body: content ?? '' };
  const match = content.match(HEADER_RE);
  if (!match) return { header: null, body: content };
  try {
    const parsed = JSON.parse(match[1]);
    if (isFileRecordHeader(parsed)) {
      return { header: parsed, body: content.slice(match[0].length) };
    }
  } catch {
    // Malformed header JSON — treat as absent rather than throwing; the whole string still renders below.
  }
  return { header: null, body: content };
}

/** The stored fields a reference-backed artifact carries in place of inline content. */
export interface FileReferenceFields {
  content?: string;
  referenceUrl?: string;
  contentType?: string;
  sizeBytes?: string;
  /** The writer's actor kind, which is what decides whether the file reads as the user's or an agent's. */
  writerType?: string;
}

/** The content string a reader should treat this file as having.
 *
 *  A file whose bytes live elsewhere keeps `content` EMPTY and addresses them through `reference.url` —
 *  the file-system rejects a write that sets both. It carries its type and its real upload size in its
 *  own fields, which is exactly what the header smuggles inside the content, so it reads out here as a
 *  header plus a URL body: byte-identical to the pointer artifacts that came before it. One shape
 *  reaches every consumer below, and none of them needs to know which kind of file it got.
 *
 *  `ownerKind` comes from the WRITER, not a guess: an upload the user attached is theirs even once an
 *  agent files it away, which is the distinction the Uploaded/Agent tabs are built on. */
export function effectiveContent(file: FileReferenceFields): string | undefined {
  if (file.content) return file.content;
  const url = file.referenceUrl;
  if (!url) return undefined;
  const size = Number(file.sizeBytes);
  const header: FileRecordHeader = {
    scope: 'workspace',
    ownerKind: file.writerType === 'USER' ? 'user' : 'agent',
    ...(file.contentType ? { mimeType: file.contentType } : {}),
    ...(Number.isFinite(size) && size > 0 ? { sizeBytes: size } : {}),
    rawUrl: url,
  };
  return `<!--sym-file:${JSON.stringify(header)}-->\n${url}`;
}

/** The agent a workspace artifact is attributed to, read off its FileRecord header — `undefined` for a
 *  file with no header, or one whose author had no agent entity.
 *
 *  BOTH halves are required, and that gate is shared 1:1 with native's `cardAgent`: the dot needs the
 *  stable id to pick its hue and the row needs a name to print, so half a pair renders nothing rather
 *  than a nameless dot. Whitespace-only counts as absent. */
export function builtByOf(content: string | undefined | null): { agentId: string; name: string } | undefined {
  const { header } = parseFileRecordContent(content);
  const agentId = header?.agentId?.trim();
  const name = header?.agentName?.trim();
  return agentId && name ? { agentId, name } : undefined;
}
